import { defineStore } from "pinia"
import { type userType, store, routerArrays, storageLocal } from "../utils"
import { router, resetRouter } from "@/router"
import { type UserResult, type RefreshTokenResult, getLogin, refreshTokenApi } from "@/api/user"
import { useMultiTagsStoreHook } from "./multiTags"
import { type DataInfo, setToken, removeToken, userKey } from "@/utils/auth"

export const useUserStore = defineStore({
  id: "pure-user",
  state: (): userType => ({
    // 头像
    avatar: storageLocal().getItem<DataInfo<number>>(userKey)?.avatar ?? "",
    // 用户名
    username: storageLocal().getItem<DataInfo<number>>(userKey)?.username ?? "",
    // 昵称
    nickname: storageLocal().getItem<DataInfo<number>>(userKey)?.nickname ?? "",
    // 页面级别权限
    roles: storageLocal().getItem<DataInfo<number>>(userKey)?.roles ?? [],
    // Maxkey ID
    maxkeyId: storageLocal().getItem<DataInfo<number>>(userKey)?.maxkeyId ?? "",
    // 是否勾选了登录页的免登录
    isRemembered: false,
    // 登录页的免登录存储几天，默认7天
    loginDay: 7
  }),
  actions: {
    /** 存储头像 */
    SET_AVATAR(avatar: string) {
      this.avatar = avatar
    },
    /** 存储用户名 */
    SET_USERNAME(username: string) {
      this.username = username
    },
    /** 存储昵称 */
    SET_NICKNAME(nickname: string) {
      this.nickname = nickname
    },
    /** 存储角色 */
    SET_ROLES(roles: Array<string>) {
      this.roles = roles
    },
    /** 存储Maxkey ID */
    SET_MAXKEYID(maxkeyId: string) {
      this.maxkeyId = maxkeyId
    },
    /** 存储是否勾选了登录页的免登录 */
    SET_ISREMEMBERED(bool: boolean) {
      this.isRemembered = bool
    },
    /** 设置登录页的免登录存储几天 */
    SET_LOGINDAY(value: number) {
      this.loginDay = Number(value)
    },
    /** 登入 */
    async loginByUsername(data) {
      return new Promise<UserResult>((resolve, reject) => {
        getLogin(data)
          .then((data) => {
            if (data?.code === 200) {
              // Transform API response to DataInfo format
              const tokenData = {
                accessToken: data.data.token,
                refreshToken: data.data.token,
                expires: new Date("2030/10/30 00:00:00"),
                username: data.data.info.account,
                nickname: data.data.info.name,
                roles: ["teacher"],
                maxkeyId: data.data.info.maxkeyId
              }
              setToken(tokenData)
            }
            resolve(data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    /** 退出登录 */
    async logOut() {
      // 调用后端退出接口记录日志
      try {
        const { teacherLogout } = await import("@/api/teacher")
        await teacherLogout()
        console.log("退出登录接口调用成功")
      } catch (error) {
        console.error("退出登录接口调用失败:", error)
        // 即使接口失败也继续本地登出
      }

      this.username = ""
      this.roles = []
      this.maxkeyId = ""
      removeToken()
      useMultiTagsStoreHook().handleTags("equal", [...routerArrays])
      resetRouter()

      // 🎯 清理课程数据，避免新用户使用前一个用户的课程信息
      import("@/store/modules/course").then(({ useCourseStore }) => {
        const courseStore = useCourseStore()
        courseStore.clearCourseData()
        console.log("用户退出：课程数据已清理")
      }).catch((error) => {
        console.warn("用户退出：清理课程数据失败:", error)
      })

      router.push("/login")
    },
    /** 刷新`token` */
    async handRefreshToken(data) {
      return new Promise<RefreshTokenResult>((resolve, reject) => {
        refreshTokenApi(data)
          .then((data) => {
            if (data) {
              setToken(data.data)
              resolve(data)
            }
          })
          .catch((error) => {
            reject(error)
          })
      })
    }
  }
})

export function useUserStoreHook() {
  return useUserStore(store)
}
